import mustache from 'mustache';
import debounce from 'lodash/debounce';

/**
 * Phonebook page client side logic
 */
class Phonebook {

  /**
   * @constructor
   * @param {HTMLElement} element - Main element of this view
   */
  constructor(element) {
    this._element = element;

    // Due moving filters outside of main element,
    // get filters element to its own member variable
    this._filtersElement = document.querySelector('.hero-banner-filters__filters');
    this._filtersElementMobile = document.querySelector('.hero-banner-filters__mobile-selected-filters');

    this._elements();
    this._bind();

    //Debounced version of load method for preventing unnecessary fetch calls.
    this.debouncedLoad = debounce(this.#load, 500, { trailing: true, leading: false });

    this.#updateMobileSelectedFilters();
  }

  /**
   * Set a ui state for loading spinner element.
   *
   * @param {boolean} value - If true is passed container will be fade a bit or false is passed container has none opacity.
   */
  set isLoading(value) {
    this._element.classList.toggle('hero-banner-filters--loading', value);
  }

  /**
   * All elements accessed in this view
   */
  _elements() {
    // Radio buttons
    this._vehicleTypeInputElements = this._filtersElement.querySelectorAll('input[name="type"]');

    // Text inputs
    this._nameInput = this._filtersElement.querySelector('input[name="name"]');
    this._phoneInput = this._filtersElement.querySelector('input[name="phone"]');

    // Multiselects
    this._cityMultiselect = window.multiselects.city;
    this._brandMultiselect = window.multiselects.brand;
    this._titleMultiselect = window.multiselects.title;

    this._mobileCloseFilters = this._filtersElement.querySelector('.hero-banner-filters__close-mobile-filters');
    this._mobileShowResults = this._filtersElement.querySelector('.hero-banner-filters__mobile-show-results');
    this._clearFiltersBtn = this._filtersElement.querySelector('.hero-banner-filters__mobile-clear-filters');

    // Ouside of filters element due styling (paddings), search it from document
    this._mobileFiltersButton = document.querySelector('.hero-banner-filters__mobile .hedin-btn');
  }

  /**
   * Bind event listeners
   */
  _bind() {
    this._vehicleTypeInputElements.forEach( element => element.addEventListener('change', this.#onFilterChange.bind(this, element)));

    this._nameInput.addEventListener('input', this.#onFilterChange.bind(this));
    this._phoneInput.addEventListener('input', this.#onFilterChange.bind(this));

    [ this._cityMultiselect, this._brandMultiselect, this._titleMultiselect ].forEach(e => {
      e.addCallback({onValueChange: this.#onFilterChange.bind(this)});
    })

    this._clearFiltersBtn?.addEventListener('click', this.#clearFilters.bind(this));

    [ this._mobileFiltersButton, this._mobileCloseFilters, this._mobileShowResults ].forEach(e => {
      e?.addEventListener('click', this.#mobileFiltersToggle.bind(this));
    });
  }

  /**
   * Toggle mobile filters on / off when mobile
   * filters button is pressed
   */
  #mobileFiltersToggle() {
    this._filtersElement.classList.toggle('hero-banner-filters--in');
  }

  /**
   * Makes a article search request
   * @async
   * @param {URLSearchParams} formData get parameters for a request
   * @return {object} responseJson object
   */
  async #fetchSearchResult( formData ) {

    try {
      //Each search has it's own AbortController instance so they don't mixup between request.
      this._controller = new window.AbortController();
      this._signal = this._controller.signal;

      const response = await fetch( this._filtersElement.dataset.searchUrl + '?' + formData.toString() , {
        method: 'GET',
        signal: this._signal,
      });

      const responseJson = await response.json();

      this.isLoading = false;

      return responseJson;
    } catch( error ) {
      if (error.name !== 'AbortError') {
        console.warn('Unhandled error:', error);
      }
    }

    this.isLoading = false;
  }


  /**
   * Clear all selected filters
   */
  #clearFilters() {
    // default radio group radio element check
    this._filtersElement.querySelector('#vehicle-types-all').checked = true;

    // Clear multiselects
    window.multiselects.city.selectOptions(['']);
    window.multiselects.brand.selectOptions(['']);
    window.multiselects.title.selectOptions(['']);

    // Clear text inputs
    this._nameInput.value = '';
    this._phoneInput.value = '';

    this.#load();
  }

  /**
   * Initial articles from the server, there is debounced version of this method reference stored to
   * this.debouncedLoad variable. Use it as possible.. to prevent unnecessary request spam.
   * Makes a request and append result to dom.
   * @async
   */
  async #load() {
    this.isLoading = true;

    const queryStringUrl = this.#generateUrl();

    this.#abortSearch();
    const formData = new URLSearchParams(queryStringUrl);

    const responseJson = await this.#fetchSearchResult(formData);

    if (responseJson) {
      let htmlRendered = '<p class="text-center p-b-md">Ei hakutuloksia</p>';

      if (responseJson?.data?.tables.length) {
        htmlRendered = mustache.render(responseJson.template, responseJson.data);
      }

      this._element.innerHTML = htmlRendered;

      history.pushState({}, '', queryStringUrl);
    }
  }

  /**
   * Called when type element is clicked
   * @param {HTMLElement} element input element that was clicked
   * @param {Event} event click event
   */
  async #onFilterChange() {
    this.#abortSearch();
    this.debouncedLoad();

    this.#updateMobileSelectedFilters();
  }

  /**
   * Update selected filters in mobile view
   */
  #updateMobileSelectedFilters() {
    const fields = [
      {
        // Type filter
        label: document.querySelector('.hero-banner-filters__radios .form-v10__field-title').innerText,
        value: Array.from(this._vehicleTypeInputElements).find(input => input.checked).nextElementSibling.innerText,
      },
      {
        // City filter
        label: this._cityMultiselect.selectDetails.defaultCaption,
        value: this._cityMultiselect.options.find(option => option.selected)?.shownValue || 'Kaikki',
      },
      {
        // Brand filter
        label: this._brandMultiselect.selectDetails.defaultCaption,
        value: this._brandMultiselect.options.find(option => option.selected)?.shownValue || 'Kaikki',
      },
      {
        // Title filter
        label: this._titleMultiselect.selectDetails.defaultCaption,
        value: this._titleMultiselect.options.find(option => option.selected)?.shownValue || 'Kaikki',
      },
      {
        // Name filter
        label: document.querySelector('.hero-banner-filters__input-field [for="phonebook-name"]').innerText,
        value: this._nameInput.value,
      },
      {
        // Phone filter
        label: document.querySelector('.hero-banner-filters__input-field [for="phonebook-phone"]').innerText,
        value: this._phoneInput.value,
      },
    ];

    let fieldsHtml = '';

    fields.forEach(field => {
      if (field.value) {
        fieldsHtml += `
          <div class="overline">${field.label}</div>
          <div class="label bold">${field.value}</div>
        `;
      }
    });

    this._filtersElementMobile.innerHTML = fieldsHtml;
  }

  /**
   * Generates URL according to selected filters
   * @return {string} URL with selected filters
   */
  #generateUrl() {
    // Get checkbox values
    const city = window.multiselects.city.getValue().filter(Boolean);
    const cityQueryString = city.length ? `city=${ city[0] }` : false;

    const brand = window.multiselects.brand.getValue().filter(Boolean);
    const brandQueryString = brand.length ? `brand=${ brand[0] }` : false;

    const title = window.multiselects.title.getValue().filter(Boolean);
    const titleQueryString = title.length ? `title=${ title[0] }` : false;

    const vehicleType = Array.from(this._vehicleTypeInputElements).find(element => element.checked)?.value;
    const vehicleTypeQueryString = vehicleType ? `type=${ vehicleType }` : false;

    const name = this._nameInput?.value;
    const nameQueryString = name ? `name=${ name }` : false;

    const phone = this._phoneInput?.value;
    const phoneQueryString = phone ? `phone=${ phone }` : false;

    const queryStringParts = [
      cityQueryString,
      brandQueryString,
      titleQueryString,
      vehicleTypeQueryString,
      nameQueryString,
      phoneQueryString
    ].filter(Boolean);

    const queryString = queryStringParts.length ? `?${ queryStringParts.join('&') }` : '';

    return queryString;
  }

  /**
   * Called on fetch request is aborted
   */
  #abortSearch() {
    if (this._controller) {
      this._controller.abort();
    }
  }

  /**
   * Initializes view in the master.js
   *
   * @return {Phonebook|undefined}  if element with #phonebook-filters id is found from dom PhonebookFilters class instance is returned.
   */
  static init() {
    const element = document.querySelector('#phonebook-results');
    if( element ) {
      return new Phonebook(element);
    }
  }
}

export default Phonebook
